import { Header, HeaderProps } from '@almbrand/header';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { PageContext } from 'context/PageContext';
import { useContext, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { removeStateFromSessionStorage } from 'utilities';
import styles from './HeaderComponent.module.scss';

interface BasketIconProps<T> {
	basketIcon?: HeaderProps<T>['basketIcon'];
}

export const HeaderComponent: React.FC<HeaderReference & BasketIconProps<any>> = ({
	basketIcon,
	logoImage,
	logoDarkImage,
	logoUrl,
	loginLabelText,
	loginUrl,
	headerLinks,
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const { isMobile } = useDeviceQuery();
	const [authButton, setAuthButton] = useState(null);
	const customer = useAppSelector((state) => state.customer);

	const mapNavigationLinks = (links: Link[]) =>
		links
			.map((link) => ({
				text: link?.text ?? '',
				title: link?.title ?? '',
				url: link?.url ?? '',
				target: link?.target ?? '',
				isExternalLink: link?.isExternalLink ?? false,
				useVueRoute: link?.useVueRoute ?? false,
			}))
			.filter((link) => link.text && link.url);

	useEffect(() => {
		if (customer.isDataFetched && customer.customerProductsFetched) {
			if (!customer.isLoggedIn) {
				setAuthButton({
					buttonType: 'personal',
					children: loginLabelText ?? 'Login',
					size: 'small',
					...(isMobile && {
						iconProps: {
							themeName: theme,
							icon: 'lock_closed',
							width: '24',
							height: '24',
							fill: 'white',
						},
					}),
					linkButtonSettings: {
						href: loginUrl?.url ?? '/auth/login',
						target: loginUrl?.target ?? '_self',
					},
				});
			} else {
				setAuthButton({
					buttonType: 'personal',
					children: 'Log ud',
					onClick: () => {
						removeStateFromSessionStorage();
						location.href = '/auth/logout?applicationId=codan&redirectUri=https://www.codan.dk';
					},
					size: 'small',
					...(isMobile && {
						iconProps: {
							themeName: theme,
							icon: 'lock_open',
							width: '24',
							height: '24',
							fill: 'white',
						},
					}),
				});
			}
		}
	}, [
		customer.isDataFetched,
		customer.customerProductsFetched,
		customer.isLoggedIn,
		isMobile,
		theme,
		loginLabelText,
		loginUrl?.url,
		loginUrl?.target,
	]);

	return (
		<div className={styles.HeaderComponent}>
			<Header
				logoProps={{ logoUrl: logoImage?.url ?? '', link: logoUrl?.url ?? '/' }}
				navigationLinks={headerLinks?.length > 0 && mapNavigationLinks(headerLinks)}
				buttonProps={authButton}
				basketIcon={basketIcon}
				renderChildren={(link): JSX.Element => (
					<NavLink
						key={link?.url}
						to={link?.url}
						title={link?.title}
					>
						{link?.text}
					</NavLink>
				)}
			/>
		</div>
	);
};
